import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import {
  CaretDown,
  Briefcase,
  User,
} from "@phosphor-icons/react"
import { cn } from "../../lib/utils"

interface QuickAction {
  id: string
  label: string
  icon: React.ElementType
  shortcut?: string
  action: () => void
}

interface ActionGroup {
  label: string
  actions: QuickAction[]
}

interface QuickActionMenuProps {
  onNewMatter: () => void
  onNewContact: () => void
}

export function QuickActionMenu({ onNewMatter, onNewContact }: QuickActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const handleNewMatter = useCallback(() => {
    onNewMatter()
    setIsOpen(false)
  }, [onNewMatter])

  const handleNewContact = useCallback(() => {
    onNewContact()
    setIsOpen(false)
  }, [onNewContact])

  const actionGroups: ActionGroup[] = useMemo(() => [
    {
      label: "CREATE",
      actions: [
        {
          id: "new-matter",
          label: "New Matter",
          icon: Briefcase,
          shortcut: "N",
          action: handleNewMatter,
        },
        {
          id: "new-contact",
          label: "New Contact",
          icon: User,
          shortcut: "C",
          action: handleNewContact,
        },
      ],
    },
  ], [handleNewMatter, handleNewContact])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  // Keyboard shortcuts when menu is open
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return

      if (event.key === "Escape") {
        setIsOpen(false)
        return
      }

      // Check for shortcut keys
      const key = event.key.toUpperCase()
      for (const group of actionGroups) {
        for (const action of group.actions) {
          if (action.shortcut === key) {
            event.preventDefault()
            action.action()
            return
          }
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, actionGroups])

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-1 px-4 py-2 text-sm font-medium",
          "text-muted-foreground hover:text-foreground transition-colors",
          isOpen && "text-foreground"
        )}
      >
        Add New
        <CaretDown
          className={cn(
            "h-3 w-3 transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <div
          className={cn(
            "absolute left-0 top-full mt-1 w-56 max-w-[calc(100vw-2rem)] rounded-xl",
            "bg-background border border-border shadow-xl",
            "py-2 z-[100]",
            "animate-flyout-enter"
          )}
        >
          {actionGroups.map((group, groupIndex) => (
            <div key={group.label}>
              {groupIndex > 0 && <div className="border-t border-border my-2" />}
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group.label}
              </div>
              {group.actions.map((action) => {
                const Icon = action.icon
                return (
                  <button
                    key={action.id}
                    onClick={action.action}
                    className={cn(
                      "flex items-center gap-3 w-full px-3 py-2 mx-0",
                      "text-sm text-foreground",
                      "transition-colors duration-100",
                      "hover:bg-muted"
                    )}
                  >
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 text-left">{action.label}</span>
                    {action.shortcut && (
                      <span className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                        {action.shortcut}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
